import React, { useEffect, useRef, useState } from "react";
import { SpannedFrame, SpannedMasonryGrid } from "@masonry-grid/react";
import { useAuthentication } from "../Authentication";
import { useSelector } from "react-redux";
import { selectAccessToken } from "../selectors";
import { TRYON_SERVER_URL } from "../config";
import ThreeDotSpinner from "../components/ThreeDotSpinner";
import SegmentedFilter from "../components/SegmentedFilter";
import { selectAuthenticated } from "../selectors";
import SignInPrompt from "../components/SignInPrompt";
import { useNavigate } from "react-router-dom";
import UploadImage from "../components/UploadImage";
import {
  fetchAllImages,
  deleteExperimentImage,
} from "../Utils/ExperimentImageUtils";
import InfiniteScroll from "react-infinite-scroll-component";
import { DownloadIcon, ImageIcon, Trash2Icon, UploadIcon, XIcon } from "lucide-react";

const MASONRY_FRAME_WIDTH = 280;
const MASONRY_GAP = 16;
const masonryInnerStyle = {
  gridAutoRows: "calc(var(--frame-width) / var(--precision))",
  margin: 0,
  width: "100%",
  maxWidth: "100%",
};
const tileInnerStyle = {
  overflow: "hidden",
  borderRadius: "12px",
  background: "#fff",
  boxShadow: "0 0 0 1px var(--hairline)",
};
function imageTypeLabel(type) {
  if (!type) return "Photo";
  if (type === "result") return "Generated";
  return type.charAt(0).toUpperCase() + type.slice(1);
}

function imagePrompt(image) {
  const value = image?.prompt || image?.description || image?.params?.prompt || "";
  return String(value).trim();
}

const tileMediaStyle = {
  position: "absolute",
  inset: 0,
  width: "100%",
  height: "100%",
  objectFit: "cover",
  objectPosition: "center",
  display: "block",
};

function probeImageSize(src) {
  return new Promise((resolve) => {
    const probe = new window.Image();
    let settled = false;
    const finish = (width, height) => {
      if (settled) return;
      settled = true;
      window.clearTimeout(timer);
      resolve({
        width: width > 0 ? width : 3,
        height: height > 0 ? height : 4,
      });
    };
    const timer = window.setTimeout(() => finish(3, 4), 8000);
    probe.onload = () => finish(probe.naturalWidth, probe.naturalHeight);
    probe.onerror = () => finish(3, 4);
    probe.src = src;
  });
}

function probeVideoSize(src) {
  return new Promise((resolve) => {
    const probe = document.createElement("video");
    let settled = false;
    const finish = (width, height) => {
      if (settled) return;
      settled = true;
      window.clearTimeout(timer);
      resolve({
        width: width > 0 ? width : 9,
        height: height > 0 ? height : 16,
      });
    };
    const timer = window.setTimeout(() => finish(9, 16), 8000);
    probe.preload = "metadata";
    probe.onloadedmetadata = () => finish(probe.videoWidth, probe.videoHeight);
    probe.onerror = () => finish(9, 16);
    probe.src = src;
  });
}
export default function MyImages() {
  const { isAuthenticated } = useAuthentication();
  const accessToken = useSelector(selectAccessToken);
  const [myImages, setMyImages] = useState([]);
  const [fetchingMyImages, setFetchingMyImages] = useState(false);
  const [myImagesFetched, setMyImagesFetched] = useState(false);
  const [error, setError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [totalImages, setTotalImages] = useState(0);
  const [nextUrl, setNextUrl] = useState(null);
  const [previousUrl, setPreviousUrl] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [imageToDelete, setImageToDelete] = useState(null);
  const [genderFilter, setGenderFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const authenticated = useSelector(selectAuthenticated);
  const navigate = useNavigate();
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [uploadImage, setUploadImage] = useState(null);
  const baseUrl = `${TRYON_SERVER_URL}/api/v1/experiment_image/?`;
  const limit = 20;
  const offset = 0;
  // const observerTarget = useRef(null);

  const [myVideos, setVideos] = useState([]);
  const [videoUrls, setVideoUrls] = useState({});
  const [totalGallery, setTotalGallery] = useState(0);
  const mediaSizesRef = useRef({});
  const [layoutTick, setLayoutTick] = useState(0);

  useEffect(() => {
    myVideos.forEach(async (gen) => {
      if (!videoUrls[gen.id]) {
        const blobUrl = await fetchVideoBlobUrl(gen);
        console.log(blobUrl);
        if (blobUrl) {
          setVideoUrls((prev) => ({ ...prev, [gen.id]: blobUrl }));
        }
      }
    });
  }, [myVideos]);

  useEffect(() => {
    if (!authenticated) return;

    if (!myImagesFetched) {
      fetchMyImages(baseUrl, {
        type: typeFilter,
        gender: genderFilter,
        limit: limit,
        offset: offset,
      });
    }

    fetchVideos();
  }, [authenticated, typeFilter, genderFilter, myImagesFetched]);

  useEffect(() => {
    if (!authenticated) return;
    
    setMyImages([]); // Clear old images
    setNextUrl(null);
    fetchMyImages(baseUrl, {
      type: typeFilter,
      gender: genderFilter,
      limit: limit,
      offset: offset,
    });
  }, [typeFilter, genderFilter, authenticated]);

  // useEffect(() => {
  //   let isSubscribed = true;
  //   let isFetching = false;

  //   const observer = new IntersectionObserver(
  //     (entries) => {
  //       if (
  //         entries[0].isIntersecting &&
  //         nextUrl &&
  //         !fetchingMyImages &&
  //         isSubscribed &&
  //         !isFetching
  //       ) {
  //         isFetching = true;
  //         fetchMyImages(nextUrl, {
  //           type: typeFilter,
  //           gender: genderFilter,
  //         }).finally(() => {
  //           isFetching = false;
  //         });
  //       }
  //     },
  //     {
  //       threshold: 0.1,
  //       rootMargin: "200px",
  //     }
  //   );

  //   const currentTarget = observerTarget.current;
  //   if (currentTarget) {
  //     observer.observe(currentTarget);
  //   }

  //   return () => {
  //     isSubscribed = false;
  //     if (currentTarget) {
  //       observer.unobserve(currentTarget);
  //     }
  //     observer.disconnect();
  //   };
  // }, [nextUrl, fetchingMyImages, typeFilter, genderFilter]);

  const fetchMyImages = async (url, params) => {
    setFetchingMyImages(true);
    setError(false);
    setErrorMessage("");
    try {
      const response = await fetchAllImages(
        url,
        accessToken,
        true,
        false,
        params
      );
      if (response && response.status === "success") {
        setMyImages((prevImages) => {
          const all = [...prevImages, ...(response.data || [])];

          // Remove duplicates by ID
          const unique = Array.from(
            new Map(all.map((img) => [img.id, img])).values()
          );

          return unique;
        });

        setTotalImages(response.count || 0);
        setNextUrl(response.next);
        setPreviousUrl(response.previous);
      } else if (response && response.message) {
        setError(true);
        setErrorMessage(response.message);
      }
    } catch (err) {
      setError(true);
      setErrorMessage(err?.message || "Failed to fetch images. Please try again.");
    } finally {
      setFetchingMyImages(false);
      setMyImagesFetched(true);
    }
  };

  const fetchVideos = async () => {
    try {
      const response = await fetch(
        `${TRYON_SERVER_URL}/api/v1/generations/`,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );

      if (!response.ok) throw new Error("Failed to fetch videos");

      const data = await response.json();

      const videos = (data.results || []).filter(
        (v) => v.params?.model === "sora" || v.params?.model === "veo-3.1"
      );

      setVideos(videos);
    } catch (error) {
      console.error("Error fetching videos:", error);
    }
  };

  const fetchVideoBlobUrl = async (gen) => {
    try {
      const model = gen.params?.model;
      let url = "";

      if (model === "sora") {
        url = `${TRYON_SERVER_URL}/api/v1/azure/video/download/?id=${gen.id}`;
      } else if (model === "veo-3.1") {
        url = `${TRYON_SERVER_URL}/api/v1/video/download/?generation_id=${gen.id}`;
      } else {
        console.warn("Unknown model:", model);
        return null;
      }

      const response = await fetch(url, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });

      if (!response.ok) {
        const text = await response.text();
        console.error("Video fetch failed:", text);
        return null;
      }

      const blob = await response.blob();
      return URL.createObjectURL(blob);
    } catch (err) {
      console.error("Error fetching video blob:", err);
      return null;
    }
  };

  const downloadImage = async (imageId, imageUrl) => {
    try {
      console.log("imageUrl:", TRYON_SERVER_URL + "/" + imageUrl);
      const response = await fetch(TRYON_SERVER_URL + "/" + imageUrl, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });

      if (response.ok) {
        const blob = await response.blob();
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = "image_" + imageId + ".png";
        link.click();
        URL.revokeObjectURL(link.href);
      } else {
        console.log(response);
        setError(true);
        setErrorMessage("Error downloading image!");
      }
    } catch (error) {
      console.log(error);
      setError(true);
      setErrorMessage("Error downloading image!");
    }
  };

  const downloadVideo = async (generation) => {
    try {
      const model = generation.params?.model;
      let downloadUrl = "";

      if (model === "sora") {
        downloadUrl = `${TRYON_SERVER_URL}/api/v1/azure/video/download/?id=${generation.id}`;
      } else if (model === "veo-3.1") {
        downloadUrl = `${TRYON_SERVER_URL}/api/v1/video/download/?generation_id=${generation.id}`;
      } else {
        console.error("Unknown model:", model);
        return;
      }

      const response = await fetch(downloadUrl, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });

      if (!response.ok) throw new Error("Download failed");

      const blob = await response.blob();
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `${generation.id}.mp4`;
      link.click();
      URL.revokeObjectURL(link.href);

      console.log("Video downloaded successfully!");
    } catch (error) {
      console.error("Error downloading video:", error);
    }
  };

  const handleDeleteImage = async (imageId) => {
    setError(false);
    setErrorMessage("");
    try {
      const response = await deleteExperimentImage(imageId, accessToken);
      if (response && response.status === "success") {
        setMyImages((prevImages) =>
          prevImages.filter((image) => image.id !== imageId)
        );
        setTotalImages(totalImages - 1);
        console.log("Image deleted successfully");
      } else if (response && response.message) {
        setError(true);
        setErrorMessage(response.message);
      }
    } catch (err) {
      setError(true);
      setErrorMessage(err?.message || "Failed to delete image. Please try again.");
    }
  };

  const confirmDelete = () => {
    handleDeleteImage(imageToDelete);
    setIsDeleteModalOpen(false);
    setImageToDelete(null);
  };

  const openDeleteModal = (imageId) => {
    setImageToDelete(imageId);
    setIsDeleteModalOpen(true);
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    setUploadImage(file);
    setIsUploadModalOpen(true);
  };

  useEffect(() => {
    const total = (myImages?.length || 0) + (myVideos?.length || 0);
    setTotalGallery(total);
  }, [myImages, myVideos]);

  useEffect(() => {
    let cancelled = false;
    const missing = myImages.filter((image) => !mediaSizesRef.current[image.id]);
    if (missing.length === 0) return undefined;

    Promise.all(
      missing.map(async (image) => {
        const size = await probeImageSize(
          `${TRYON_SERVER_URL}/${image.image_url}`
        );
        mediaSizesRef.current[image.id] = size;
      })
    ).then(() => {
      if (!cancelled) setLayoutTick((tick) => tick + 1);
    });

    return () => {
      cancelled = true;
    };
  }, [myImages]);

  useEffect(() => {
    let cancelled = false;
    const missing = myVideos.filter(
      (video) => videoUrls[video.id] && !mediaSizesRef.current[video.id]
    );
    if (missing.length === 0) return undefined;

    Promise.all(
      missing.map(async (video) => {
        const size = await probeVideoSize(videoUrls[video.id]);
        mediaSizesRef.current[video.id] = size;
      })
    ).then(() => {
      if (!cancelled) setLayoutTick((tick) => tick + 1);
    });

    return () => {
      cancelled = true;
    };
  }, [myVideos, videoUrls]);

  const readyImages = myImages.flatMap((image) => {
    const size = layoutTick >= 0 ? mediaSizesRef.current[image.id] : undefined;
    return size ? [{ image, size }] : [];
  });

  const readyVideos = myVideos.flatMap((video) => {
    const size = layoutTick >= 0 ? mediaSizesRef.current[video.id] : undefined;
    const src = videoUrls[video.id];
    return size && src ? [{ video, size, src }] : [];
  });

  useEffect(() => {
    if (!selectedImage) return undefined;
    const onKey = (event) => {
      if (event.key === "Escape") setSelectedImage(null);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [selectedImage]);

  if (authenticated === false) {
    return (
      <div className="flex min-h-[calc(100vh-5rem)] items-center justify-center bg-ground p-4">
        <div className="w-full max-w-md">
          <SignInPrompt
            title="Sign in to see your gallery"
            message="Uploaded photos will show up here."
            onSignIn={() => navigate("/signin")}
          />
        </div>
      </div>
    );
  }

  return (


    <div
      id="scrollableDiv"
      className="relative flex h-screen min-w-0 flex-col overflow-x-hidden overflow-y-auto bg-ground p-4"
    >
      <div className="flex flex-wrap items-center justify-between gap-x-6 gap-y-3">
        <div className="flex items-center justify-start">
          <h1 className="mr-4 whitespace-nowrap font-Mulish text-lg font-semibold text-secondary">
            Gallery ({myImages.length + myVideos.length})
          </h1>

          {authenticated && (
            <button
              className="flex items-center gap-2 rounded-full border border-primary-200 bg-white px-4 py-2 font-Mulish text-sm text-primary-700 hover:bg-primary-50"
              onClick={() => setIsUploadModalOpen(true)}
            >
              <UploadIcon className="h-4 w-4" />
              Upload
            </button>
          )}
        </div>

        {authenticated && (
          <div className="flex flex-wrap items-center gap-x-6 gap-y-3">
            <SegmentedFilter
              label="Type"
              value={typeFilter}
              onChange={setTypeFilter}
              options={[
                { value: "all", label: "All" },
                { value: "model", label: "Model" },
                { value: "garment", label: "Garment" },
                { value: "result", label: "Generated" },
              ]}
            />
            <SegmentedFilter
              label="Gender"
              value={genderFilter}
              onChange={setGenderFilter}
              options={[
                { value: "all", label: "All" },
                { value: "male", label: "Male" },
                { value: "female", label: "Female" },
              ]}
            />
          </div>
        )}
      </div>

      <div className="mb-4 mt-3 border-t border-hairline" />

      {authenticated &&
        readyImages.length === 0 &&
        readyVideos.length === 0 &&
        (fetchingMyImages || myImages.length > 0 || myVideos.length > 0) && (
          <SpannedMasonryGrid
            frameWidth={MASONRY_FRAME_WIDTH}
            gap={MASONRY_GAP}
            precision={20}
            className="w-full"
            innerStyle={masonryInnerStyle}
          >
            {Array.from({ length: 8 }, (_, index) => (
              <SpannedFrame
                key={index}
                width={3}
                height={4}
                innerStyle={{ overflow: "hidden", borderRadius: "12px" }}
              >
                <div className="h-full w-full animate-pulse bg-primary-50" />
              </SpannedFrame>
            ))}
          </SpannedMasonryGrid>
        )}


      {/* <ResponsiveMasonry
        columnsCountBreakPoints={{ 350: 1, 750: 2, 900: 3, 1200: 4 }}
      >
        <Masonry gutter="16px">
          {myImages.map((image) => (
            <div
              key={image.id}
              className="break-inside-avoid bg-white rounded-xl shadow-[0_0_0_1px_var(--hairline)] group overflow-hidden cursor-pointer"
            >
              <div className="relative w-full flex items-center justify-center">
                <LazyLoadImage
                  alt={image.name}
                  src={TRYON_SERVER_URL + "/" + image.image_url}
                  effect="opacity"
                  className="w-full h-auto object-cover rounded-xl transform group-hover:scale-105 transition-all duration-500"
                  wrapperClassName="w-full flex items-center justify-center"
                  beforeLoad={() => (
                    <div className="w-full aspect-square bg-gradient-to-br from-gray-200 to-gray-300 animate-pulse flex items-center justify-center">
                      <ThreeDotSpinner />
                    </div>
                  )}
                  afterLoad={() => {}}
                  threshold={100}
                  visibleByDefault={false}
                />

                <div
                  className="absolute inset-0 flex cursor-pointer flex-col justify-between"
                  onClick={() => setSelectedImage(image)}
                >
                  <div className="absolute top-2.5 right-2.5 flex justify-end items-center gap-2">
                    <button
                      className="flex h-8 w-8 items-center justify-center rounded-full bg-white text-primary-700 shadow-[0_0_0_1px_var(--hairline)]"
                      onClick={(e) => {
                        e.stopPropagation();
                        downloadImage(image.id, image.image_url);
                      }}
                      title="Download Image"
                    >
                      <DownloadIcon className="w-5 h-5 lg:w-4 lg:h-4" />
                    </button>
                    <button
                      className="flex h-8 w-8 items-center justify-center rounded-full bg-white text-primary-700 shadow-[0_0_0_1px_var(--hairline)]"
                      onClick={(e) => {
                        e.stopPropagation();
                        openDeleteModal(image.id);
                      }}
                      title="Delete Image"
                    >
                      <Trash2Icon className="w-5 h-5 lg:w-4 lg:h-4" />
                    </button>
                  </div>

                  <div className="absolute bottom-0 left-0 p-2 flex flex-wrap justify-start items-center gap-1.5">
                    {image.type !== null && (
                      <div className="rounded-full bg-primary-50 px-2 py-0.5 font-Mulish text-xs text-primary-700">
                        {image.type === "result"
                          ? "Generated"
                          : image.type?.charAt(0).toUpperCase() +
                            image.type?.slice(1)}
                      </div>
                    )}
                    {image.gender && (
                      <div className="rounded-full bg-primary-50 px-2 py-0.5 font-Mulish text-xs text-primary-700">
                        {image.gender.charAt(0).toUpperCase() +
                          image.gender.slice(1)}
                      </div>
                    )}
                    <div className="rounded-full bg-primary-50 px-2 py-0.5 font-Mulish text-xs text-primary-700">
                      {image.access.charAt(0).toUpperCase() +
                        image.access.slice(1)}
                    </div>
                    <div className="rounded-full bg-primary-50 px-2 py-0.5 font-Mulish text-xs text-primary-700">
                      {new Date(image.created_on).toLocaleString("en-GB", {
                        day: "2-digit",
                        month: "short",
                        hour: "2-digit",
                        minute: "2-digit",
                        hour12: true,
                      })}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}

          {myVideos.map((gen) => (
            <div
              key={gen.id}
              className="break-inside-avoid bg-white rounded-xl shadow-[0_0_0_1px_var(--hairline)] group overflow-hidden cursor-pointer"
            >
              <div className="relative w-full flex items-center justify-center">
                <video
                  src={videoUrls[gen.id]}
                  controls
                  className="w-full h-auto object-cover rounded-xl"
                />
                <div className="absolute top-2.5 right-2.5 flex justify-end items-center gap-2">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      downloadVideo(gen);
                    }}
                    className="flex h-8 w-8 items-center justify-center rounded-full bg-white text-primary-700 shadow-[0_0_0_1px_var(--hairline)]"
                    title="Download Video"
                  >
                    <DownloadIcon className="w-5 h-5" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </Masonry>
      </ResponsiveMasonry> */}
      

      {authenticated && (readyImages.length > 0 || readyVideos.length > 0) && (
        <InfiniteScroll
          dataLength={myImages.length + myVideos.length}
          next={() =>
            fetchMyImages(nextUrl, { type: typeFilter, gender: genderFilter })
          }
          hasMore={!!nextUrl}
          loader={
            <div className="my-4 flex h-20 w-full items-center justify-center">
              <ThreeDotSpinner />
            </div>
          }
          endMessage={
            myImages.length > 0 && (
              <p className="mt-4 text-center font-Mulish text-sm text-graphite">That's everything.</p>
            )
          }
          scrollableTarget="scrollableDiv"
        >
          <SpannedMasonryGrid
            frameWidth={MASONRY_FRAME_WIDTH}
            gap={MASONRY_GAP}
            precision={20}
            className="w-full"
            innerStyle={masonryInnerStyle}
          >
            {readyImages.map(({ image, size }) => (
              <SpannedFrame
                key={image.id}
                width={size.width}
                height={size.height}
                className="group cursor-pointer"
                innerStyle={tileInnerStyle}
              >
                <img
                  alt={image.name || "Gallery image"}
                  src={`${TRYON_SERVER_URL}/${image.image_url}`}
                  style={tileMediaStyle}
                />

                <div
                  className="absolute inset-0 flex cursor-pointer flex-col justify-between"
                  onClick={() => setSelectedImage(image)}
                >
                  <div className="absolute right-2.5 top-2.5 flex items-center justify-end gap-2 opacity-0 pointer-events-none transition-opacity duration-200 group-hover:pointer-events-auto group-hover:opacity-100">
                    <button
                      className="flex h-8 w-8 items-center justify-center rounded-full bg-white text-primary-700 shadow-[0_0_0_1px_var(--hairline)]"
                      onClick={(e) => {
                        e.stopPropagation();
                        downloadImage(image.id, image.image_url);
                      }}
                      title="Download Image"
                    >
                      <DownloadIcon className="h-5 w-5 lg:h-4 lg:w-4" />
                    </button>
                    <button
                      className="flex h-8 w-8 items-center justify-center rounded-full bg-white text-primary-700 shadow-[0_0_0_1px_var(--hairline)]"
                      onClick={(e) => {
                        e.stopPropagation();
                        openDeleteModal(image.id);
                      }}
                      title="Delete Image"
                    >
                      <Trash2Icon className="h-5 w-5 lg:h-4 lg:w-4" />
                    </button>
                  </div>
                </div>
              </SpannedFrame>
            ))}

            {readyVideos.map(({ video, size, src }) => (
              <SpannedFrame
                key={video.id}
                width={size.width}
                height={size.height}
                className="group cursor-pointer"
                innerStyle={tileInnerStyle}
              >
                <video
                  src={src}
                  controls
                  style={tileMediaStyle}
                />
                <div className="absolute right-2.5 top-2.5 flex items-center justify-end gap-2 opacity-0 pointer-events-none transition-opacity duration-200 group-hover:pointer-events-auto group-hover:opacity-100">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      downloadVideo(video);
                    }}
                    className="flex h-8 w-8 items-center justify-center rounded-full bg-white text-primary-700 shadow-[0_0_0_1px_var(--hairline)]"
                    title="Download Video"
                  >
                    <DownloadIcon className="h-5 w-5" />
                  </button>
                </div>
              </SpannedFrame>
            ))}
          </SpannedMasonryGrid>
        </InfiniteScroll>
      )}
      
      {authenticated && myImagesFetched && myImages.length === 0 && myVideos.length === 0 && (
        <div className="mb-2 flex w-full flex-col items-center justify-center rounded-2xl bg-primary-50 p-8">
          <ImageIcon className="mb-3 h-8 w-8 text-primary-700" />
          <div className="text-center font-Mulish text-lg font-semibold text-secondary">
            No images yet
          </div>
          <span className="mt-1 text-center font-Mulish text-sm text-graphite">
            Uploaded photos will show up here.
          </span>
        </div>
      )}

      {!fetchingMyImages && (
        <div className="hidden flex justify-center items-center mt-4 gap-4 absolute bottom-4 left-0 right-0 mx-auto">
          {previousUrl && myImages.length !== 0 && (
            <button
              className="w-32 rounded-full bg-primary-700 px-4 py-2 font-Mulish text-sm font-semibold text-white"
              onClick={() => fetchMyImages(previousUrl, {})}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={2}
                stroke="currentColor"
                className="w-4 h-4 inline-block"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M15.75 19.5 8.25 12l7.5-7.5"
                />
              </svg>
              {" Previous"}
            </button>
          )}
          {nextUrl && myImages.length !== 0 ? (
            <button
              className="w-32 rounded-full bg-primary-700 px-4 py-2 font-Mulish text-sm font-semibold text-white"
              onClick={() => fetchMyImages(nextUrl, {})}
            >
              {"Next "}
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={2}
                stroke="currentColor"
                className="w-4 h-4 inline-block"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M8.25 4.5 15.75 12 8.25 19.5"
                />
              </svg>
            </button>
          ) : (
            <></>
          )}
        </div>
      )}

      {error && errorMessage && !errorMessage.includes("Cannot read properties") && (
        <div className="text-red-500 p-3 bg-red-50 border border-red-200 rounded-lg mb-4">
          {errorMessage}
        </div>
      )}

      {selectedImage && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-overlay p-4"
          onClick={() => setSelectedImage(null)}
        >
          <div
            className="relative flex max-h-[calc(100dvh-2rem)] w-full max-w-7xl flex-col overflow-hidden rounded-2xl bg-white shadow-[0_0_0_1px_rgba(15,1,52,0.08)] lg:h-[min(640px,calc(100vh-2rem))] lg:flex-row"
            onClick={(event) => event.stopPropagation()}
          >
          <div className="relative h-[42vh] w-full shrink-0 overflow-hidden bg-ground lg:h-full lg:min-h-0 lg:w-auto lg:flex-1">
            <img
              src={`${TRYON_SERVER_URL}/${selectedImage.image_url}`}
              alt={selectedImage.name || "Gallery image"}
              className="absolute left-3 top-3 h-[calc(100%-1.5rem)] w-[calc(100%-1.5rem)] object-contain lg:left-4 lg:top-4 lg:h-[calc(100%-2rem)] lg:w-[calc(100%-2rem)]"
            />
            <button
              type="button"
              className="absolute right-3 top-3 flex h-8 w-8 items-center justify-center rounded-full border border-primary-200 bg-white text-primary-700 hover:bg-primary-50 lg:hidden"
              onClick={() => setSelectedImage(null)}
              title="Close"
            >
              <XIcon className="h-4 w-4" />
            </button>
          </div>
          <aside className="flex min-h-0 w-full flex-1 flex-col overflow-y-auto bg-white p-4 lg:h-full lg:w-[320px] lg:flex-none lg:p-5 lg:shadow-[inset_1px_0_0_rgba(15,1,52,0.08)]">
            <div className="flex items-center justify-between gap-3">
              <p className="font-Mulish text-lg font-semibold text-secondary">Parameters</p>
              <button
                type="button"
                className="hidden h-8 w-8 shrink-0 items-center justify-center rounded-full border border-primary-200 bg-white text-primary-700 hover:bg-primary-50 lg:flex"
                onClick={() => setSelectedImage(null)}
                title="Close"
              >
                <XIcon className="h-4 w-4" />
              </button>
            </div>

            {imagePrompt(selectedImage) && (
              <div className="mt-3">
                <p className="font-Mulish text-xs text-graphite">Prompt</p>
                <p className="mt-2 font-Mulish text-sm text-secondary">
                  {imagePrompt(selectedImage)}
                </p>
              </div>
            )}

            <div className="mt-3">
              <div className="flex flex-wrap gap-2">
                <span className="rounded-full bg-primary-50 px-2.5 py-1 font-Mulish text-xs text-primary-700">
                  {imageTypeLabel(selectedImage.type)}
                </span>
                {selectedImage.gender && (
                  <span className="rounded-full bg-primary-50 px-2.5 py-1 font-Mulish text-xs text-primary-700">
                    {selectedImage.gender.charAt(0).toUpperCase() + selectedImage.gender.slice(1)}
                  </span>
                )}
                {selectedImage.access && (
                  <span className="rounded-full bg-primary-50 px-2.5 py-1 font-Mulish text-xs text-primary-700">
                    {selectedImage.access.charAt(0).toUpperCase() + selectedImage.access.slice(1)}
                  </span>
                )}
              </div>
              {selectedImage.name && (
                <p className="mt-3 font-Mulish text-sm text-secondary">{selectedImage.name}</p>
              )}
              <p className="mt-3 font-Mulish text-xs text-graphite">
                {new Date(selectedImage.created_on).toLocaleString("en-GB", {
                  day: "2-digit",
                  month: "short",
                  year: "numeric",
                  hour: "2-digit",
                  minute: "2-digit",
                  hour12: true,
                })}
              </p>
            </div>

            <div className="mt-4 flex flex-col gap-2 lg:mt-auto lg:pt-8">
              <button
                type="button"
                className="w-full rounded-full bg-primary-700 px-4 py-2.5 font-Mulish text-sm font-semibold text-white"
                onClick={() => downloadImage(selectedImage.id, selectedImage.image_url)}
              >
                Download
              </button>
              <button
                type="button"
                className="w-full rounded-full border border-primary-200 bg-white px-4 py-2.5 font-Mulish text-sm text-primary-700 hover:bg-primary-50"
                onClick={() => {
                  openDeleteModal(selectedImage.id);
                  setSelectedImage(null);
                }}
              >
                Delete
              </button>
            </div>
          </aside>
          </div>
        </div>
      )}

      {isDeleteModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-overlay">
          <div className="mx-4 w-full rounded-2xl bg-white p-6 shadow-[0_0_0_1px_var(--hairline)] lg:w-1/3">
            <h2 className="mb-2 font-Mulish text-lg font-semibold text-secondary">Delete this photo?</h2>
            <p className="font-Mulish text-sm text-graphite">
              This also removes any result made from it.
            </p>
            <div className="mt-4 flex justify-end gap-2">
              <button
                className="rounded-full border border-primary-200 bg-white px-4 py-2 font-Mulish text-sm text-primary-700 hover:bg-primary-50"
                onClick={() => setIsDeleteModalOpen(false)}
              >
                Cancel
              </button>
              <button
                className="rounded-full bg-primary-700 px-4 py-2 font-Mulish text-sm font-semibold text-white"
                onClick={confirmDelete}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {isUploadModalOpen && (
        <UploadImage
          imageType="all"
          allImages={myImages}
          setAllImages={setMyImages}
          closeModal={() => setIsUploadModalOpen(false)}
        />
      )}

      {/* Loading indicator and observer target */}
      {/* <div
        ref={observerTarget}
        className="w-full h-20 flex items-center justify-center mt-4"
      >
        {fetchingMyImages && (
          <div className="flex items-center justify-center">
            <div className="animate-pulse h-screen w-full bg-gray-500 border-b-2 border-primary"></div>
          </div>
        )}
      </div>

      {!nextUrl && myImages.length > 0 && (
        <div className="text-center text-gray-500 mt-4">
          No more images to load
        </div>
      )} */}
    </div>
  );
}
